import { useEffect, useState } from 'react';
import {
  Home,
  Compass,
  Bell,
  Mail,
  Bookmark,
  User,
  Settings,
  LogOut,
  ArrowLeft,
  X,
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useMessages } from '@/contexts/MessageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/ui/Avatar';
import { ROUTES } from '@/constants';

const primaryNav = [
  { label: 'Home', short: 'Home', href: ROUTES.HOME, icon: Home },
  { label: 'Explore', short: 'Explore', href: ROUTES.SEARCH, icon: Compass },
  { label: 'Notifications', short: 'Alerts', href: ROUTES.NOTIFICATIONS, icon: Bell },
  { label: 'Messages', short: 'Chats', href: ROUTES.MESSAGES, icon: Mail },
  { label: 'Profile', short: 'Profile', href: '/@me', icon: User },
];

const secondaryNav = [
  { label: 'Bookmarks', href: ROUTES.BOOKMARKS, icon: Bookmark },
  { label: 'Settings', href: ROUTES.SETTINGS, icon: Settings },
];

function isActivePath(label: string, pathname: string) {
  if (label === 'Home') return pathname === '/';
  if (label === 'Explore') return pathname.startsWith('/search');
  if (label === 'Notifications') return pathname.startsWith('/notifications');
  if (label === 'Messages') return pathname.startsWith('/messages');
  if (label === 'Profile') return pathname.startsWith('/@');
  return false;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, profile, signOut } = useAuth();
  const { unreadCount } = useNotifications();
  const { totalUnread } = useMessages();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Close the account sheet whenever the route changes.
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';
  const isMessages = location.pathname.startsWith(ROUTES.MESSAGES);

  return (
    <div className="flex h-[100dvh] bg-neutral-50 dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50 overflow-hidden selection:bg-brand-500 selection:text-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shrink-0 z-20">
        <Link to={ROUTES.HOME} className="flex items-center gap-3 px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
          <div className="h-8 w-8 rounded-xl bg-brand-600 flex items-center justify-center text-white font-bold text-lg">K</div>
          <span className="text-xl font-bold tracking-tight">KeLike</span>
        </Link>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const active = isActivePath(item.label, location.pathname);
            const badge =
              item.label === 'Notifications'
                ? unreadCount
                : item.label === 'Messages'
                  ? totalUnread
                  : 0;
            return (
              <Link
                key={item.label}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                  active
                    ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                )}
              >
                <Icon size={18} />
                <span className="flex-1">{item.label}</span>
                {badge > 0 && (
                  <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            );
          })}
          <div className="pt-3 mt-3 border-t border-neutral-200 dark:border-neutral-800 space-y-1">
            {secondaryNav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                    active
                      ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                      : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  )}
                >
                  <Icon size={18} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="px-3 py-4 border-t border-neutral-200 dark:border-neutral-800">
          <button
            onClick={signOut}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0 relative">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-2 px-3 sm:px-4 md:px-6 py-3 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur">
          <div className="flex items-center gap-1 md:hidden">
            {!isHome && (
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-1 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <Link to={ROUTES.HOME} className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-xl bg-brand-600 flex items-center justify-center text-white font-bold text-sm">K</div>
              <span className="text-lg font-bold tracking-tight">KeLike</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
            <div className="h-7 w-7 rounded-xl bg-brand-600 flex items-center justify-center text-white font-bold">K</div>
            <span className="text-lg font-bold tracking-tight text-neutral-900 dark:text-neutral-100">KeLike</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>
            {user && (
              <button
                onClick={() => setSheetOpen(true)}
                className="md:hidden p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800"
                aria-label="Open account menu"
              >
                <UserAvatar src={profile?.avatar_url} size="sm" />
              </button>
            )}
          </div>
        </header>

        <div
          className={cn(
            'flex-1 min-h-0 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-6',
            // No outside padding on mobile so posts and other full-bleed
            // components can span the full width. Desktop keeps its padding.
            isMessages ? 'flex flex-col overflow-hidden' : 'overflow-y-auto md:p-6',
          )}
        >
          {children}
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="grid grid-cols-5 h-16">
          {primaryNav.map((item) => {
            const Icon = item.icon;
            const active = isActivePath(item.label, location.pathname);
            const badge =
              item.label === 'Notifications'
                ? unreadCount
                : item.label === 'Messages'
                  ? totalUnread
                  : 0;
            return (
              <Link
                key={item.label}
                to={item.href}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 px-1 transition-colors',
                  active
                    ? 'text-brand-600 dark:text-brand-400'
                    : 'text-neutral-500 dark:text-neutral-400',
                )}
              >
                <span className="relative">
                  <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                  {badge > 0 && (
                    <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold leading-none text-white">
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </span>
                <span className={cn('w-full truncate text-center text-[10px] leading-none', active ? 'font-semibold' : 'font-normal')}>
                  {item.short}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Mobile account sheet */}
      <AnimatePresence>
        {sheetOpen && (
          <div className="md:hidden fixed inset-0 z-40">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40"
              onClick={() => setSheetOpen(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="absolute inset-x-0 bottom-0 rounded-t-3xl bg-white dark:bg-neutral-900 p-4"
              style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
              role="dialog"
              aria-modal="true"
              aria-label="Account menu"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <UserAvatar src={profile?.avatar_url} size="md" />
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{profile?.display_name || user?.email}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">@{profile?.username}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSheetOpen(false)}
                  className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mt-2 divide-y divide-neutral-200 dark:divide-neutral-800">
                <Link to="/@me" className="flex items-center gap-3 py-3.5 text-sm font-medium">
                  <User size={18} /> Profile
                </Link>
                {secondaryNav.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.label} to={item.href} className="flex items-center gap-3 py-3.5 text-sm font-medium">
                      <Icon size={18} /> {item.label}
                    </Link>
                  );
                })}
                <button
                  onClick={() => {
                    setSheetOpen(false);
                    signOut();
                  }}
                  className="flex w-full items-center gap-3 py-3.5 text-sm font-medium text-red-500"
                >
                  <LogOut size={18} /> Log out
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  );
}
